import React, { useMemo } from "react";
import { useField } from "formik";
import { useEntities } from "@hooks/useEntities";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { Account } from "@common/types/domains/accounting/account";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import {
    useWorkspaceAccountGroups,
    useWorkspaceAccounts,
    useWorkspaceAccrualAccounts,
} from "@api/account.api";
import { TransactionRuleActionFormSchema } from "../constants";
import { MultiEntityAccountCombobox } from "@components/accounts/MultiEntityAccountCombobox";
import { findFirstResolvingAccount } from "@components/accounts/resolveAccountsForIds";
import { useAccountTargetEntities } from "../useEntitiesFromFormCondition";
import {
    buildAccrualAccountActionValue,
    buildCashAccountActionValue,
} from "./accountActionValue";
import styles from "./ActionForm.module.scss";

interface Props {
    index: number;
    controlButtonSize?: TriggerButtonProps["size"];
    scopedEntityIds?: number[];
}

type MultiEntityComboboxProps = Pick<
    React.ComponentProps<typeof MultiEntityAccountCombobox>,
    | "identity"
    | "coverage"
    | "groupsByEntityId"
    | "groupsLoading"
    | "allowManuallyCreatedAcrossEntities"
    | "hideEntityIcons"
    | "selectedAccountIds"
>;

const PersonalEntityWarning: React.FC = () => (
    <span>Personal entities are not supported for Account assignment</span>
);

function useAccountActionShared(
    index: number,
    accounts: Array<Account & { entityId: number }> | undefined,
    scopedEntityIds?: number[],
) {
    const [field, , helpers] = useField<
        TransactionRuleActionFormSchema["value"]
    >(`actions.${index}.value`);

    const entities = useAccountTargetEntities({ scopedEntityIds });
    const allEntities = useEntities();
    const accountGroups = useWorkspaceAccountGroups();

    const selectedAccountName = useMemo(
        () =>
            field.value?.accountName ??
            accounts?.find((account) => account.id === field.value?.accountId)
                ?.name ??
            findFirstResolvingAccount(field.value?.accountIds ?? [], accounts)
                ?.name,
        [field, accounts],
    );

    const selectedEntityIds = useMemo(
        () =>
            entities.length > 0
                ? entities.map((e) => e.id)
                : allEntities.map((e) => e.id),
        [entities, allEntities],
    );

    const multiEntityComboboxProps: MultiEntityComboboxProps = {
        identity: "nameTypePath",
        coverage: "all",
        hideEntityIcons: true,
        groupsByEntityId: accountGroups.data,
        groupsLoading: accountGroups.isLoading,
        allowManuallyCreatedAcrossEntities: true,
        selectedAccountIds:
            field.value?.accountIds ??
            (field.value?.accountId ? [field.value.accountId] : undefined),
    };

    return {
        field,
        helpers,
        multiEntityComboboxProps,
        selectedAccountName,
        selectedEntityIds,
        hasPersonalEntity: entities.some((e) => e.isPersonal),
    };
}

export const CashAccountActionForm: React.FC<Props> = ({
    index,
    controlButtonSize,
    scopedEntityIds,
}) => {
    const cashAccounts = useWorkspaceAccounts();
    const {
        helpers,
        multiEntityComboboxProps,
        selectedAccountName,
        selectedEntityIds,
        hasPersonalEntity,
    } = useAccountActionShared(index, cashAccounts.data, scopedEntityIds);

    if (hasPersonalEntity) {
        return <PersonalEntityWarning />;
    }

    return (
        <MultiEntityAccountCombobox
            selectedAccountName={selectedAccountName}
            onChange={(account) =>
                helpers.setValue(buildCashAccountActionValue({ account }))
            }
            selectedEntityIds={selectedEntityIds}
            size={controlButtonSize ?? "md"}
            accounts={cashAccounts.data}
            accountsLoading={cashAccounts.isLoading}
            {...multiEntityComboboxProps}
        />
    );
};

export const AccrualAccountActionForm: React.FC<Props> = ({
    index,
    controlButtonSize,
    scopedEntityIds,
}) => {
    const accrualAccounts = useWorkspaceAccrualAccounts();
    const {
        field,
        helpers,
        multiEntityComboboxProps,
        selectedAccountName,
        selectedEntityIds,
        hasPersonalEntity,
    } = useAccountActionShared(index, accrualAccounts.data, scopedEntityIds);

    if (hasPersonalEntity) {
        return <PersonalEntityWarning />;
    }

    return (
        <>
            <MultiEntityAccountCombobox
                selectedAccountName={selectedAccountName}
                onChange={(account) =>
                    helpers.setValue(
                        buildAccrualAccountActionValue({
                            account,
                            autoConfirm: field.value?.autoConfirm,
                        }),
                    )
                }
                selectedEntityIds={selectedEntityIds}
                size={controlButtonSize ?? "md"}
                accounts={accrualAccounts.data}
                accountsLoading={accrualAccounts.isLoading}
                {...multiEntityComboboxProps}
            />
            <Tooltip content="Automatically post transactions that meet rule conditions to the accrual ledger">
                <span className={styles.autoConfirmCheckbox}>
                    <Checkbox
                        checked={!!field.value?.autoConfirm}
                        label={
                            <span className={styles.autoConfirmLabel}>
                                Auto confirm
                            </span>
                        }
                        onCheckedChange={(checked) =>
                            helpers.setValue({
                                ...field.value,
                                autoConfirm: checked,
                            })
                        }
                    />
                </span>
            </Tooltip>
        </>
    );
};
